import React, {Fragment, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import IssueFieldInlineEdit from './IssueFieldInlineEdit';
import {issueAttributesType} from '../../constants/proptype-validation';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';
import {formatSubtypeDisplayValue} from '../../utils/issueTypes-helpers';
import {hasPermittedAttribute} from '../../utils/issues-helpers';
import ChangeIssueTypeModal from './ChangeIssueTypeModal';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import useChangeIssueTypeAffectedFields from './useChangeIssueTypeAffectedFields';
import SelectBoxAdapter from '../common/SelectBoxAdapter';

const useShouldShowIssueTypeModal = ({fieldIssueTypeEntitiesMap, issueCustomAttributeMappings, issue}) => {
  const {
    doesNewTypeIncludeCurrentStatus,
    getIssueCustomAttributesThatWillBeLost,
    getNewCustomAttributeMappings
  } = useChangeIssueTypeAffectedFields({
    fieldIssueTypeEntitiesMap,
    issueCustomAttributeMappings,
    issue
  });
  return useCallback(
    issueSubtype =>
      featureFlags.get(FEATURE_FLAGS.CHANGE_ISSUE_TYPE) &&
      (!doesNewTypeIncludeCurrentStatus(issueSubtype.parentId) ||
        getIssueCustomAttributesThatWillBeLost(issueSubtype.id).length > 0 ||
        getNewCustomAttributeMappings(issueSubtype.id).length > 0),
    [doesNewTypeIncludeCurrentStatus, getIssueCustomAttributesThatWillBeLost, getNewCustomAttributeMappings]
  );
};

const IssueSubtypeInlineEdit = ({
  issue,
  fieldIssueTypeEntitiesMap,
  fieldIssueSubTypes,
  className,
  activeIssueSubTypesGroupedDropdownOptions,
  dropdownMaxWidth,
  issueCustomAttributeMappings,
  styleType,
  container
}) => {
  const getDisplayValue = useCallback(
    currentValue => formatSubtypeDisplayValue(currentValue, fieldIssueTypeEntitiesMap),
    [fieldIssueTypeEntitiesMap]
  );

  const getIssueAttributes = useCallback(value => {
    const patchAttributesObj = {[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: value};
    const currentIssueSubType = fieldIssueTypeEntitiesMap[value];

    // update issue id if subtype has changed from new issue
    if (issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID] !== currentIssueSubType.parentId) {
      patchAttributesObj[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID] = currentIssueSubType.parentId;
    }

    return patchAttributesObj;
  }, [fieldIssueTypeEntitiesMap, issue]);

  const getOptions = useCallback(currentSubTypeId => {
    // If user have permission edit type - show all types available
    if (hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID)) {
      return activeIssueSubTypesGroupedDropdownOptions;
    }

    const currentOption = fieldIssueSubTypes.find(option => option.id === currentSubTypeId);
    let subtypeOptions = fieldIssueSubTypes;
    const issueTypeId = fieldIssueTypeEntitiesMap[currentSubTypeId].parentId;
    const issueType = fieldIssueTypeEntitiesMap[issueTypeId];
    if (!currentOption) {
      // If current option is inactive - we still need to show it.
      const optionToAdd = fieldIssueTypeEntitiesMap[currentSubTypeId];
      if (optionToAdd) {
        subtypeOptions = sortAlphabeticallyByPath([...fieldIssueSubTypes, optionToAdd], 'title');
      }
    }
    return [[issueType.title, subtypeOptions]];
  }, [activeIssueSubTypesGroupedDropdownOptions, fieldIssueSubTypes, fieldIssueTypeEntitiesMap, issue]);
  const shouldShowChangeIssueTypeModal = useShouldShowIssueTypeModal({
    fieldIssueTypeEntitiesMap,
    issueCustomAttributeMappings,
    issue
  });

  const [pendingIssueSubtypeId, setPendingIssueSubtypeId] = useState(undefined);

  return (
    <Fragment>
      <IssueFieldInlineEdit
        issue={issue}
        attributeName={ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID}
        label={i18n.t('new.issueTypeAndSubType')}
        getDisplayValue={getDisplayValue}
        placeholder={i18n.t('unspecifiedValue')}
        className={className}
        data-testid="IssueSubTypeInlineEdit"
        styleType={styleType}
        getIssueAttributes={getIssueAttributes}
      >
        {(currentValue, closeEditMode, updateIssue) => (
          <SelectBoxAdapter
            selectedOptions={fieldIssueTypeEntitiesMap[currentValue]}
            autofocus
            grouped
            valueKey="id"
            labelKey="title"
            dropdownMaxWidth={dropdownMaxWidth}
            options={getOptions(currentValue)}
            onClose={closeEditMode}
            onChange={option => {
              if (shouldShowChangeIssueTypeModal(option)) {
                setPendingIssueSubtypeId(option.id);
              } else {
                updateIssue(option.id);
              }
            }}
            searchable
            searchIcon
            searchPlaceholder={i18n.t('filter.issueTypeSearchPlaceholder')}
          />
        )}
      </IssueFieldInlineEdit>
      <ChangeIssueTypeModal
        issue={issue}
        issueSubtypeId={pendingIssueSubtypeId}
        onClose={() => setPendingIssueSubtypeId(undefined)}
        container={container}
      />
    </Fragment>
  );
};

IssueSubtypeInlineEdit.displayName = 'IssueSubtypeInlineEdit';

IssueSubtypeInlineEdit.propTypes = {
  issue: issueAttributesType.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  fieldIssueSubTypes: PropTypes.array.isRequired,
  className: PropTypes.string,
  activeIssueSubTypesGroupedDropdownOptions: PropTypes.array,
  dropdownMaxWidth: PropTypes.number,
  styleType: PropTypes.oneOf(Object.values(IssueFieldInlineEdit.StyleTypes)),
  issueCustomAttributeMappings: PropTypes.object,
  container: PropTypes.object
};

export default IssueSubtypeInlineEdit;
